import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { useThemeContext } from '../../contexts/ThemeContext';

const DURATION_OPTIONS_MINUTES = [
  30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, 390, 420, 450, 480, 510, 540, 570, 600, 630,
  660, 690, 720,
];

export interface TutorBlockTimeDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  setTitle: (title: string) => void;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  selectedTime: Date | null;
  setSelectedTime: (time: Date | null) => void;
  durationMinutes: number;
  setDurationMinutes: (duration: number) => void;
  isPast: boolean;
  hasConflict: boolean;
  submitting?: boolean;
  onSubmit: () => void;
}

const fieldSx = (mode: string) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
    },
  },
});

export default function TutorBlockTimeDialog({
  open,
  onClose,
  title,
  setTitle,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  durationMinutes,
  setDurationMinutes,
  isPast,
  hasConflict,
  submitting = false,
  onSubmit,
}: TutorBlockTimeDialogProps) {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const sx = fieldSx(mode);

  const getCurrentLocale = () => {
    const lang = t('calendar.locale', 'en');
    if (lang === 'pl') return plPL;
    return enGB;
  };

  const canSubmit =
    Boolean(selectedDate && selectedTime) && !hasConflict && !isPast && !submitting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          margin: { xs: 1, sm: 2 },
          maxWidth: { xs: 'calc(100% - 16px)', sm: '600px' },
          boxShadow:
            mode === 'dark' ? '0px 8px 32px rgba(0, 0, 0, 0.6)' : '0px 4px 20px rgba(0, 0, 0, 0.12)',
          background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.5,
          pt: 2.5,
          px: 3,
          '& .MuiTypography-root': {
            fontWeight: 700,
            fontSize: '1.35rem',
            color: mode === 'dark' ? '#ECECFB' : '#212529',
            letterSpacing: '-0.02em',
          },
        }}
      >
        {t('spaceCalendar.createEvent', 'Block Time')}
      </DialogTitle>
      <Divider
        sx={{
          mx: 3,
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        }}
      />
      <DialogContent sx={{ pt: 3, px: 3, pb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getCurrentLocale()}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%' }}>
            <TextField
              label={t('spaceCalendar.blockTitleOptional', 'Title (optional)')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              sx={sx}
            />

            <FormControl fullWidth sx={sx}>
              <InputLabel>{t('spaceCalendar.duration')}</InputLabel>
              <Select
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                label={t('spaceCalendar.duration')}
              >
                {DURATION_OPTIONS_MINUTES.map((mins) => (
                  <MenuItem key={mins} value={mins}>
                    {mins >= 60
                      ? `${Math.floor(mins / 60)} ${
                          Math.floor(mins / 60) > 1
                            ? t('spaceCalendar.hours')
                            : t('spaceCalendar.hour')
                        }${
                          mins % 60 ? ` ${mins % 60} ${t('spaceCalendar.min')}` : ''
                        }`
                      : `${mins} ${t('spaceCalendar.min')}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
                width: '100%',
              }}
            >
              <DatePicker
                label={t('spaceCalendar.date')}
                value={selectedDate}
                onChange={setSelectedDate}
                disablePast
                slotProps={{ textField: { fullWidth: true, sx } }}
              />
              <TimePicker
                label={t('spaceCalendar.time')}
                value={selectedTime}
                onChange={setSelectedTime}
                ampm={false}
                slotProps={{ textField: { fullWidth: true, sx } }}
              />
            </Box>

            {isPast ? (
              <Alert severity="error" sx={{ borderRadius: 2, fontWeight: 500 }}>
                {t('spaceCalendar.pastBookingWarning')}
              </Alert>
            ) : null}

            {hasConflict && !isPast ? (
              <Alert severity="error" sx={{ borderRadius: 2, fontWeight: 500 }}>
                {t(
                  'tutorCalendar.slotConflictWarning',
                  'This slot conflicts with an existing lesson or block.',
                )}
              </Alert>
            ) : null}

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t(
                'spaceCalendar.blockEventHint',
                'Creates a calendar block only — no booking, payment, or student record.',
              )}
            </Alert>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <Divider
        sx={{
          mx: 3,
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        }}
      />
      <DialogActions sx={{ p: 3, gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={submitting}
          sx={{
            borderRadius: 2,
            textTransform: 'uppercase',
            fontWeight: 600,
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : '#4E67EB',
            color: mode === 'dark' ? '#ECECFB' : '#4E67EB',
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          sx={{
            borderRadius: 2,
            textTransform: 'uppercase',
            fontWeight: 600,
            backgroundColor: '#4E67EB',
            color: '#ffffff',
            '&.Mui-disabled': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          {t('spaceCalendar.createEvent', 'Block Time')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
